import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  Plugins,
  Expressions,
  type NormalizedAtomConfig,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {dissoc} from '@packages/utilities/key-map';
import {getName} from '@packages/systems/wfdl-Qualified';
import type {QueryContext, QueryPath, BindingPaths} from '../types';

import {
  getElementBindingDataPaths,
  __DEPRECATED__getBindingDataPaths,
  getDynTokenBindingPaths,
  type BindingDataPaths,
} from '@packages/systems/dynamo/utils/BindingUtils';
import {
  getItemRefSlug,
  getValueFieldSlug,
} from '@packages/systems/dynamo/utils/ParamFieldPathUtils';
import {getConditionFieldsExpr} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {getDynamoBindingPaths} from '@packages/systems/dynamo/wfdl';

// Extracts Dynamo binding paths from element data and returns
// the corresponding query path fragments in the given query context.
export function getQueryPathFragments(
  element: ExpressionElement,
  queryContext: QueryContext,
  plugins: Plugins.PluginsType
): QueryPath[] {
  const queryPathFragments: Array<QueryPath> = [];

  // we use appendAsQueryPaths to minimize creation of intermediate arrays
  // when processing binding paths to query paths
  appendAsQueryPaths(
    queryContext,
    getLegacyBindingPaths(element),
    queryPathFragments
  );
  appendAsQueryPaths(
    queryContext,
    getStaticBindings(element, plugins),
    queryPathFragments
  );
  appendAsQueryPaths(
    queryContext,
    getConditionPaths(element),
    queryPathFragments
  );
  appendAsQueryPaths(
    queryContext,
    getTokenBindingPaths(element, plugins),
    queryPathFragments
  );
  appendAsQueryPaths(
    queryContext,
    getGenericBindingPaths(element),
    queryPathFragments
  );

  return queryPathFragments;
}

function appendAsQueryPaths(
  queryContext: QueryContext,
  bindingPaths: BindingPaths,
  mut_outputArray: QueryPath[]
) {
  for (const bindingPath of bindingPaths) {
    const queryPath = queryContext.getQueryPath(bindingPath);
    if (queryPath) {
      mut_outputArray.push(queryPath);
    }
  }
}

function getLegacyBindingPaths(element: ExpressionElement): BindingPaths {
  const bindingDataPaths = getElementBindingDataPaths(element);
  return toBindingPaths(
    // We ignore the node.data.dyn.bind.token here and handle the
    // token bindings using `getDynTokenBindingPaths`. The data
    // structure of `node.data.dyn.bind.token` has all the used
    // fields but it is not a traditional binding path that we
    // can use to generate a query path with. Instead, we use the
    // `getDynTokenBindingPaths` that uses the `node.data.embed.meta.html`
    // content as the source of truth for HTML embed tokens.
    dissoc('token')(bindingDataPaths)
  );
}

function getStaticBindings(
  element: ExpressionElement,
  plugins: Plugins.PluginsType
): BindingPaths {
  const staticBindings = getAtom(element, plugins)?.staticBindings;

  if (!staticBindings) {
    return [];
  }

  return toBindingPaths(__DEPRECATED__getBindingDataPaths(staticBindings));
}

const toBindingPaths = (bindingDataPaths: BindingDataPaths): BindingPaths => {
  return Object.values(bindingDataPaths);
};

function getConditionPaths(element: ExpressionElement): BindingPaths {
  const conditionFieldsExpr = getConditionFieldsExpr(element);
  const conditionFields = conditionFieldsExpr
    ? Expressions.getRecord(conditionFieldsExpr)
    : null;

  if (!conditionFields) {
    return [];
  }

  return Object.keys(conditionFields).map((conditionFieldPath) => {
    const itemRefSlug = getItemRefSlug(conditionFieldPath);
    const valueFieldSlug = getValueFieldSlug(conditionFieldPath);
    return itemRefSlug ? [itemRefSlug, valueFieldSlug] : [valueFieldSlug];
  });
}

function getTokenBindingPaths(
  element: ExpressionElement,
  plugins: Plugins.PluginsType
): BindingPaths {
  const replaceable = getReplaceableStringFromElement(element, plugins);
  return getDynTokenBindingPaths(replaceable, getName(element.type));
}

function getGenericBindingPaths(element: ExpressionElement): BindingPaths {
  const elementData = Expressions.getElementData(element);
  return getDynamoBindingPaths(elementData);
}

const getReplaceableStringFromElement = (
  element: ExpressionElement,
  plugins: Plugins.PluginsType
): string => {
  const pathToReplaceableString =
    getAtom(element, plugins)?.pathToReplaceableString || [];

  if (pathToReplaceableString.length === 0) {
    return '';
  }
  const path: ExpressionPath = pathToReplaceableString.map((at) => ({
    in: 'Record',
    at,
  }));
  return Expressions.getElementDataAt(element, path, 'Text', '');
};

function getAtom(
  element: ExpressionElement,
  plugins: Plugins.PluginsType
): NormalizedAtomConfig | undefined {
  // We use `Plugin.getAtoms` instead of `Plugins.getAtom` to match
  // pre-expressions query builder functionality. `Plugins.getAtom`
  // has fallback values that would provide a slight change in behavior.
  return Plugins.getAtoms(plugins).get(element.type);
}
